// frontend/src/utils/practitionerQuickLookUtils.js

import { safeText, normalizeTypeForUi } from "@utils/utils";
import {
  toNumberOrNull,
  isValidPay,
  isValidPct,
  validatePctField,
  parseOtherProfit,
  serializeOtherProfit,
  parseOtherDeduction,
  serializeOtherDeduction,
  OTHER_DEDUCTION_MODE_HOURLY,
  validateNetsuiteId,
  PRACTITIONER_NETSUITE_FIELDS,
} from "@utils/practitionerFormUtils";

/** Lowercase values for API / native select */
function normalizePaidBasedOnForForm(val) {
  const v = safeText(val).toLowerCase();
  if (v === "collection" || v === "production") return v;
  return "";
}

export const PAID_BASED_ON_OPTIONS = [
  { value: "collection", label: "Collection" },
  { value: "production", label: "Production" },
];

export const BASE_PAY_INITIAL_ROWS = 4;
export const OTHER_PROFIT_INITIAL_ROWS = 4;
export const OTHER_DEDUCTION_INITIAL_ROWS = 4;

/**
 * ✅ Reads ALL base_pay rows from backend array: [{base_pay, pay_percent}, ...]
 * Returns an array of {base_pay, pay_percent} strings, padded to at least BASE_PAY_INITIAL_ROWS.
 */
function readAllBasePay(practitioner) {
  const arr = practitioner?.base_pay;
  const rows = [];

  if (Array.isArray(arr)) {
    for (const entry of arr) {
      if (entry && (entry.base_pay !== undefined || entry.pay_percent !== undefined)) {
        rows.push({
          base_pay: safeText(entry.base_pay),
          pay_percent: safeText(entry.pay_percent),
        });
      }
    }
  }

  while (rows.length < BASE_PAY_INITIAL_ROWS) {
    rows.push({ base_pay: "", pay_percent: "" });
  }

  return rows;
}

export const INITIAL_FORM = {
  name: "",
  practitioner_type: "",
  status: "active",
  actual_name: "",
  email: "",
  provider_code: "",
  // Identity/employment (per-clinic or per-profile — visible to all roles including PL)
  position_id: "",
  associate_id: "",
  rate_type: "",
  hire_date: "",
  supervisor: "",
  paid_based_on: "",
  associate_fee_percentage: "",

  per_day: false,
  per_day_value: "",
  insurance: "",
  grid: false,
  include_hyg: false,

  // NetSuite IDs (admin/organizer only)
  netsuite_vendor_id: "",
  netsuite_revenue_id: "",
  netsuite_af_expense_id: "",
  netsuite_lab_expense_id: "",
  netsuite_invisalign_id: "",

  // Other profit (checkbox toggle + tiered rows of { label, percent }; backend stores JSON array string)
  other_profit_enabled: false,
  other_profit_rows: Array.from({ length: OTHER_PROFIT_INITIAL_ROWS }, () => ({
    label: "",
    percent: "",
  })),
  __other_profit_rows_visible: 1,

  // Other deduction (checkbox toggle + tiered rows of { label, mode, hour_rate };
  // backend stores JSON array string). `mode` picks how the month's figure is
  // entered — an hourly rate here x hours per month, or an exact dollar amount.
  other_deduction_enabled: false,
  other_deduction_rows: Array.from({ length: OTHER_DEDUCTION_INITIAL_ROWS }, () => ({
    label: "",
    mode: OTHER_DEDUCTION_MODE_HOURLY,
    hour_rate: "",
  })),
  __other_deduction_rows_visible: 1,

  base_pay_rows: Array.from({ length: BASE_PAY_INITIAL_ROWS }, () => ({
    base_pay: "",
    pay_percent: "",
  })),
  __grid_rows_visible: 1,
};

export function practitionerToForm(practitioner) {
  if (!practitioner)
    return {
      ...INITIAL_FORM,
      base_pay_rows: INITIAL_FORM.base_pay_rows.map((r) => ({ ...r })),
      other_profit_rows: INITIAL_FORM.other_profit_rows.map((r) => ({ ...r })),
      other_deduction_rows: INITIAL_FORM.other_deduction_rows.map((r) => ({ ...r })),
    };

  const rows = readAllBasePay(practitioner);
  const filledCount = rows.filter((r) => r.base_pay !== "" || r.pay_percent !== "").length;

  const otherProfit = parseOtherProfit(practitioner?.other_profit);
  const otherProfitRows = [...otherProfit.rows];
  while (otherProfitRows.length < OTHER_PROFIT_INITIAL_ROWS) {
    otherProfitRows.push({ label: "", percent: "" });
  }
  const otherProfitFilled = otherProfit.rows.length;

  const otherDeduction = parseOtherDeduction(practitioner?.other_deduction);
  const otherDeductionRows = [...otherDeduction.rows];
  while (otherDeductionRows.length < OTHER_DEDUCTION_INITIAL_ROWS) {
    otherDeductionRows.push({ label: "", mode: OTHER_DEDUCTION_MODE_HOURLY, hour_rate: "" });
  }
  const otherDeductionFilled = otherDeduction.rows.length;

  return {
    name: safeText(practitioner?.name),
    practitioner_type: normalizeTypeForUi(practitioner?.practitioner_type).toLowerCase() || "",
    status: (safeText(practitioner?.status) || "active").toLowerCase(),
    actual_name: safeText(practitioner?.actual_name),
    email: safeText(practitioner?.email),
    provider_code: safeText(practitioner?.provider_code),
    // Identity/employment fields
    position_id: safeText(practitioner?.position_id),
    associate_id: safeText(practitioner?.associate_id),
    rate_type: safeText(practitioner?.rate_type).toLowerCase() || "",
    // hire_date comes from the API as "YYYY-MM-DD" — keep as-is for the HTML date input
    hire_date: safeText(practitioner?.hire_date),
    supervisor: safeText(practitioner?.supervisor),
    paid_based_on: normalizePaidBasedOnForForm(practitioner?.paid_based_on),
    associate_fee_percentage: safeText(practitioner?.associate_fee_percentage),

    per_day: !!practitioner?.per_day,
    per_day_value: safeText(practitioner?.per_day_value),
    insurance: safeText(practitioner?.insurance),
    grid: !!practitioner?.grid,
    include_hyg: !!practitioner?.include_hyg,

    netsuite_vendor_id: safeText(practitioner?.netsuite_vendor_id),
    netsuite_revenue_id: safeText(practitioner?.netsuite_revenue_id),
    netsuite_af_expense_id: safeText(practitioner?.netsuite_af_expense_id),
    netsuite_lab_expense_id: safeText(practitioner?.netsuite_lab_expense_id),
    netsuite_invisalign_id: safeText(practitioner?.netsuite_invisalign_id),

    other_profit_enabled: otherProfit.enabled,
    other_profit_rows: otherProfitRows,
    __other_profit_rows_visible: Math.max(1, otherProfitFilled),

    other_deduction_enabled: otherDeduction.enabled,
    other_deduction_rows: otherDeductionRows,
    __other_deduction_rows_visible: Math.max(1, otherDeductionFilled),

    base_pay_rows: rows,
    __grid_rows_visible: Math.max(1, filledCount),
  };
}

/**
 * ✅ IMPORTANT:
 * - Use SAME validation rules as Create (base pay >= 0, % 0..100)
 * - Associate fee % also 0..100
 * - Never wipe backend field due to invalid text in input
 */
export function buildUpdatePayload(form, originalPractitioner) {
  const orig = originalPractitioner || {};

  const textOrNull = (v) => safeText(v) || null;

  const payload = {
    practitioner_type: safeText(form?.practitioner_type).toLowerCase(),
    status: safeText(form?.status) || "active",
    actual_name: textOrNull(form?.actual_name),
    email: textOrNull(form?.email),
    provider_code: textOrNull(form?.provider_code),
    // Identity/employment fields (not financial — included in all edit flows)
    name: safeText(form?.name).trim(),
    position_id: textOrNull(form?.position_id),
    associate_id: textOrNull(form?.associate_id),
    rate_type: safeText(form?.rate_type).toLowerCase() || null,
    hire_date: safeText(form?.hire_date).trim() || null,
    supervisor: safeText(form?.supervisor).trim() || null,
    paid_based_on: textOrNull(form?.paid_based_on),
  };

  // ✅ Associate fee % — SAME validation logic as Base Pay %
  const feeRaw = safeText(form?.associate_fee_percentage);
  const feeMeta = validatePctField(feeRaw);

  // empty => allow clearing
  if (!feeMeta.hasAny) {
    payload.associate_fee_percentage = null;
  } else if (feeMeta.ok) {
    payload.associate_fee_percentage = feeMeta.n;
  }
  // else invalid => omit field (UI should block save)

  // ✅ only send booleans if changed
  if (!!form?.per_day !== !!orig?.per_day) payload.per_day = !!form?.per_day;

  payload.per_day_value = toNumberOrNull(safeText(form?.per_day_value));
  payload.insurance = toNumberOrNull(safeText(form?.insurance));

  if (!!form?.grid !== !!orig?.grid) payload.grid = !!form?.grid;
  if (!!form?.include_hyg !== !!orig?.include_hyg) payload.include_hyg = !!form?.include_hyg;

  const gridValue = payload.grid ?? !!orig.grid;

  // ✅ base pay only meaningful when grid=true — send ALL valid rows
  if (gridValue && Array.isArray(form?.base_pay_rows)) {
    const rows = [];

    for (const row of form.base_pay_rows) {
      const bpRaw = safeText(row?.base_pay);
      const ppRaw = safeText(row?.pay_percent);

      const hasAny = bpRaw !== "" || ppRaw !== "";
      if (!hasAny) continue;

      const bp = toNumberOrNull(bpRaw);
      const pp = toNumberOrNull(ppRaw);

      // must be both present + valid
      if (bp === null || pp === null) continue;
      if (!isValidPay(bp) || !isValidPct(pp)) continue;

      rows.push({ base_pay: bp, pay_percent: pp });
    }

    if (rows.length > 0) payload.base_pay = rows;
  }

  // ✅ Other profit — JSON array string or null (to clear)
  payload.other_profit = serializeOtherProfit({
    enabled: !!form?.other_profit_enabled,
    rows: form?.other_profit_rows,
  });

  // ✅ Other deduction — JSON array string or null (to clear)
  payload.other_deduction = serializeOtherDeduction({
    enabled: !!form?.other_deduction_enabled,
    rows: form?.other_deduction_rows,
  });

  // ✅ NetSuite IDs — only included when valid (positive integer) or explicitly cleared.
  // Caller decides whether to render these fields (admin/organizer only); we still
  // forward present values so a manager edit doesn't drop them.
  for (const { key } of PRACTITIONER_NETSUITE_FIELDS) {
    const raw = safeText(form?.[key]);
    if (raw === "") {
      payload[key] = null; // cleared
      continue;
    }
    const meta = validateNetsuiteId(raw);
    if (meta.ok) {
      payload[key] = String(meta.n);
    }
    // invalid -> omit (UI should block save)
  }

  return payload;
}

/**
 * Practitioner Lead — PATCH body when the UI hides financial fields (frontend-only contract).
 *
 * Sent fields (match "personal / basic" requirement):
 *   name, practitioner_type, status, actual_name (corporation), email, provider_code,
 *   position_id, associate_id, rate_type, hire_date, supervisor (identity fields, not financial)
 *
 * `name` lives on the shared profile, so a rename applies at every clinic the
 * practitioner works at — not only the one this modal was opened from.
 *
 * Intentionally NOT sent (must not be changed from this restricted flow):
 *   paid_based_on, associate_fee_percentage, per_day, per_day_value, insurance,
 *   grid, include_hyg, base_pay — use full buildUpdatePayload for those.
 */
export function buildPractitionerPersonalUpdatePayload(form) {
  const textOrNull = (v) => safeText(v) || null;
  return {
    name: safeText(form?.name).trim(),
    practitioner_type: safeText(form?.practitioner_type).toLowerCase(),
    status: safeText(form?.status) || "active",
    actual_name: textOrNull(form?.actual_name),
    email: textOrNull(form?.email),
    provider_code: textOrNull(form?.provider_code),
    // Identity/employment fields are not financial — safe to include for Practice Lead
    position_id: textOrNull(form?.position_id),
    associate_id: textOrNull(form?.associate_id),
    rate_type: safeText(form?.rate_type).toLowerCase() || null,
    hire_date: safeText(form?.hire_date).trim() || null,
    supervisor: safeText(form?.supervisor).trim() || null,
  };
}
